// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

/**
 * Who can open a whole workspace vault (PLAN.md P8-T03), from the vault lists: its vault-wide
 * grants, each with its access level, and a row to give a team or person access. Vault admins
 * only (GET grants answers others with 403); folders and notes have the Access dialog in the vault.
 */
import { ApiError, unwrap, type components } from "@granite/api-client";
import { Button, Dialog, DialogContent } from "@granite/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../lib/api.js";
import { errorMessage } from "../lib/errors.js";
import { t, type MessageKey } from "../lib/i18n.js";
import type { Vault } from "../lib/vaults.js";

type Access = components["schemas"]["Grant"]["access"];

// The Access dialog's labels (workspace/ui/AccessPanels.tsx).
const levelLabel: Record<Access, MessageKey> = {
  read: "access.level.read",
  comment: "access.level.comment",
  write: "access.level.write",
  admin: "access.level.admin",
};

export function VaultAccessDialog({ vault, onClose }: { vault: Vault; onClose: () => void }) {
  const qc = useQueryClient();
  const vpath = { vault_id: vault.id };
  const wsId = vault.workspace_id ?? "";
  const grants = useQuery({
    queryKey: ["grants", vault.id],
    queryFn: async () => unwrap(await api.GET("/v1/vaults/{vault_id}/grants", { params: { path: vpath } })),
    retry: false,
  });
  const canManage = grants.isSuccess;
  const members = useQuery({
    queryKey: ["members", wsId],
    enabled: wsId !== "" && canManage,
    queryFn: async () => unwrap(await api.GET("/v1/workspaces/{workspace_id}/members", { params: { path: { workspace_id: wsId } } })),
  });
  const teams = useQuery({
    queryKey: ["groups", wsId],
    enabled: wsId !== "" && canManage,
    queryFn: async () => unwrap(await api.GET("/v1/workspaces/{workspace_id}/groups", { params: { path: { workspace_id: wsId } } })),
  });
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ["grants", vault.id] });
    await qc.invalidateQueries({ queryKey: ["path-access", vault.id] });
    await qc.invalidateQueries({ queryKey: ["note-info", vault.id] });
  };
  // Granting the same team or person again changes their access.
  const grant = useMutation({
    mutationFn: async ({ type, id, access }: { type: "user" | "group"; id: string; access: Access }) => {
      unwrap(await api.POST("/v1/vaults/{vault_id}/grants", { params: { path: vpath }, body: { principal_type: type, principal_id: id, access, folder: "" } }));
    },
    onSuccess: refresh,
  });
  const revoke = useMutation({
    mutationFn: async (id: string) => {
      unwrap(await api.DELETE("/v1/vaults/{vault_id}/grants/{grant_id}", { params: { path: { ...vpath, grant_id: id } } }));
    },
    onSuccess: refresh,
  });
  const [who, setWho] = useState("");
  const [level, setLevel] = useState<Access>("read");
  const denied = grants.error instanceof ApiError && grants.error.status === 403;
  const error = (denied ? null : grants.error) ?? grant.error ?? revoke.error;
  const whole = (grants.data?.items ?? []).filter((g) => g.folder === "");
  const name = (type: string, id: string) => {
    if (type === "group") return t("access.kind.team", { name: teams.data?.items.find((g) => g.id === id)?.name ?? "" });
    const m = members.data?.items.find((x) => x.user_id === id);
    return m?.name.trim() || m?.email || t("vaultUi.sharing.someone");
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent title={t("access.dialog.title", { name: vault.name })} className="w-[min(96vw,36rem)]">
        {error && (
          <p role="alert" className="mb-2 text-sm text-danger">
            {errorMessage(error)}
          </p>
        )}
        <div className="flex flex-col gap-4 text-sm">
          {grants.isPending && <p role="status" className="text-fg-muted">{t("common.loading")}</p>}
          {denied && <p className="text-fg-muted">{t("vaultMenu.accessDenied")}</p>}
          {canManage && (
            <section aria-labelledby="vault-access-who" className="flex flex-col gap-1">
              <h3 id="vault-access-who" className="font-medium text-fg">
                {t("access.info.sharedWith")}
              </h3>
              <p className="text-fg-muted">{t("vaultMenu.accessIntro")}</p>
              {whole.length === 0 ? (
                <p className="py-1.5 text-fg-muted">{t("vaultMenu.accessNone")}</p>
              ) : (
                <ul aria-label={t("access.info.sharedWith")} className="flex flex-col divide-y divide-border">
                  {whole.map((g) => {
                    const who = name(g.principal_type, g.principal_id);
                    return (
                      <li key={g.id} className="flex items-center justify-between gap-2 py-1.5">
                        <span className="min-w-0 truncate text-fg">{who}</span>
                        <span className="flex shrink-0 items-center gap-2">
                          <select
                            aria-label={t("vaultMenu.levelOf", { name: who })}
                            className="g-input w-auto"
                            value={g.access}
                            disabled={grant.isPending}
                            onChange={(e) => {
                              grant.mutate({ type: g.principal_type, id: g.principal_id, access: e.target.value as Access });
                            }}
                          >
                            {(Object.keys(levelLabel) as Access[]).map((l) => (
                              <option key={l} value={l}>
                                {t(levelLabel[l])}
                              </option>
                            ))}
                          </select>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => {
                              revoke.mutate(g.id);
                            }}
                          >
                            {t("access.dialog.remove")}
                          </Button>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          )}
          {canManage && (
            <form
              className="flex flex-wrap items-end gap-2"
              aria-label={t("access.dialog.add")}
              onSubmit={(e) => {
                e.preventDefault();
                const [type, id] = who.split(":") as ["user" | "group", string];
                grant.mutate({ type, id, access: level }, { onSuccess: () => { setWho(""); } });
              }}
            >
              <label className="flex flex-col gap-1 text-fg">
                {t("access.dialog.who")}
                <select className="g-input w-auto" value={who} onChange={(e) => { setWho(e.target.value); }}>
                  <option value="">{t("access.dialog.choose")}</option>
                  <optgroup label={t("access.dialog.teams")}>
                    {(teams.data?.items ?? []).map((g) => (
                      <option key={g.id} value={`group:${g.id}`}>
                        {g.name}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label={t("access.dialog.people")}>
                    {(members.data?.items ?? [])
                      .filter((m) => m.role !== "owner" && m.role !== "admin")
                      .map((m) => (
                        <option key={m.user_id} value={`user:${m.user_id}`}>
                          {m.name ? `${m.name} (${m.email})` : m.email}
                        </option>
                      ))}
                  </optgroup>
                </select>
              </label>
              <label className="flex flex-col gap-1 text-fg">
                {t("access.dialog.level")}
                <select className="g-input w-auto" value={level} onChange={(e) => { setLevel(e.target.value as Access); }}>
                  {(Object.keys(levelLabel) as Access[]).map((l) => (
                    <option key={l} value={l}>
                      {t(levelLabel[l])}
                    </option>
                  ))}
                </select>
              </label>
              <Button type="submit" disabled={who === ""} loading={grant.isPending}>
                {t("access.dialog.add")}
              </Button>
            </form>
          )}
          <div className="flex justify-end">
            <Button variant="secondary" onClick={onClose}>
              {t("access.dialog.done")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
